<template>
  <div>
    <vx-card class="mb-4">

      <div class="flex justify-end mb-1 mt-1 gap-2">
        <b-button @click="showNewsletterPopup = true" variant="outline-secondary">{{$t('action.add')}}</b-button>
      </div>
      <!-- Lista Compacta -->
      <div class="compact-list">
        <div class="list-header">
          <span class="list-counter">({{ timelines.length }} registros)</span>
        </div>

        <div class="list-items">
          <div v-for="(item, index) in timelines" :key="item.id" class="list-item">
            <div class="item-content">
              <div class="item-counter">{{ index + 1 }}</div>
              <div class="item-title">{{ item.newsletter.title }}</div>
              <div class="item-dates">
                <div class="date-field">
                  <label class="date-label">Início</label>
                  <input
                    type="date"
                    v-model="item.start_at"
                    class="form-control date-input"
                    :min="minDate"
                    :max="item.end_at || undefined"
                    @change="validateStartDate(item)"
                    @input="checkCanSave"
                  />
                </div>
                <div class="date-field">
                  <label class="date-label">Fim</label>
                  <input
                    type="date"
                    v-model="item.end_at"
                    class="form-control date-input"
                    :min="item.start_at || minDate"
                    @change="validateEndDate(item)"
                    @input="checkCanSave"
                  />
                </div>
              </div>
              <div class="item-actions">
                 <b-button
                     variant="outline-info"
                     size="sm"
                     @click="openCheckpointPopup(item)"
                     v-b-tooltip.hover
                     :title="$t('newsletter.workflow.checkpoint.title')"
                     :disabled="!canManageCheckpoints(item)">
                     <i class="feather icon-check-circle"></i>
                   </b-button>
                  <vs-dropdown
                      vs-custom-content
                      vs-trigger-click class="cursor-pointer ml1 dropdown-aligned">
                    <feather-icon
                      type="filled"
                      icon="MoreVerticalIcon"
                      class="h-5 w-5 stroke-current dropdown-icon">
                    </feather-icon>
                    <vs-dropdown-menu>
                      <vs-dropdown-item>
                        <div
                          class="flex dropdown-remove-item"
                          style="width: 150px;" @click="confirmRemoveItem(item)">
                          <div class="w-2/12">
                            <feather-icon
                                icon="TrashIcon"
                                svgClasses="h-5 w-5 mb-1 stroke-current text-danger"/>
                          </div>

                          <div class="w-10/12">
                            <div class="flex text-danger font-medium">
                              {{ $t('action.remove') }}
                            </div>
                          </div>
                        </div>
                      </vs-dropdown-item>
                    </vs-dropdown-menu>
                </vs-dropdown>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex justify-end mb-2 mt-2 gap-2">
        <b-button @click="saveTimeline" variant="primary" :disabled="!canSaveTimeline">{{$t('action.save')}}</b-button>
        <b-button
          variant="outline-secondary"
          class="float-right"
          @click="goBack">
          <feather-icon
            icon="CornerUpLeftIcon"
            class="mr-50"/>
          <span class="align-middle">{{$t('back')}}</span>
        </b-button>
      </div>
      <newsletter-popup
        v-model="showNewsletterPopup"
        @selected-items="selectedItems"
      />

      <!-- Popup de Checkpoint -->
      <b-modal
        id="checkpoint-modal"
        v-model="showCheckpointPopup"
        :title="$t('newsletter.workflow.checkpoint.title')"
        size="md"
        :ok-only="true"
        :ok-title="$t('action.ok')"
        @ok="saveCheckpointDates"
        @cancel="closeCheckpointPopup"
        @hide="closeCheckpointPopup">

         <div v-if="currentTimelineItem">
           <div class="mb-3">
             <h6 class="text-primary">{{ currentTimelineItem.newsletter.title }}</h6>
             <small class="text-muted">{{ $t('newsletter.workflow.checkpoint.description') }}</small>
           </div>

           <!-- Período do cronograma -->
           <div class="timeline-period mb-3">
             <div class="period-info">
               <i class="feather icon-calendar text-info"></i>
               <span class="period-label">Período do cronograma:</span>
               <span class="period-dates">
                 <strong>{{ formatDateDisplay(currentTimelineItem.start_at) }}</strong>
                 <span class="period-separator">até</span>
                 <strong>{{ formatDateDisplay(currentTimelineItem.end_at) }}</strong>
               </span>
             </div>
             <div class="period-note">
               <small class="text-muted">
                 <i class="feather icon-info"></i>
                 As datas de verificação devem estar dentro deste período
               </small>
             </div>
           </div>

          <div class="checkpoint-list">
            <div
              v-for="(checkpoint, index) in checkpointDates"
              :key="index"
              class="checkpoint-item">
              <div class="checkpoint-date">
                <input
                  type="date"
                  v-model="checkpoint.date"
                  class="form-control"
                  :min="currentTimelineItem.start_at || minDate"
                  :max="currentTimelineItem.end_at"
                  @change="validateCheckpointDate(checkpoint, index)"
                />
              </div>
              <div class="checkpoint-actions">
                <b-button
                  variant="outline-danger"
                  size="sm"
                  @click="removeCheckpoint(index)"
                  :title="$t('action.remove')">
                  <i class="feather icon-trash-2"></i>
                </b-button>
              </div>
            </div>

            <div v-if="checkpointDates.length === 0" class="no-checkpoints">
              <p class="text-muted text-center">{{ $t('newsletter.workflow.checkpoint.no_dates') }}</p>
            </div>
          </div>

          <div class="mt-1 float-right gap-2 ">
            <b-button
              variant="outline-primary"
              size="sm"
              @click="addNewCheckpoint">
              <i class="feather icon-plus"></i>
              {{ $t('newsletter.workflow.checkpoint.add_date') }}
            </b-button>
          </div>
        </div>
      </b-modal>
    </vx-card>
  </div>
</template>

<script>

import standard from '@/services/standard'
import { isAdmin } from '@/util/Util'
import {
  BForm, BButton, BFormGroup, BInputGroup, BFormTextarea, BCardText, BFormInput,
  BFormDatepicker, VBTooltip
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, min, max} from '@validations'
import Ripple from 'vue-ripple-directive'
import SelectSuggestion from '@/components/SelectSuggestion.vue'

import NewsletterPopup from '@/views/apps/newsletter/NewsletterPopup.vue'

import NewsletterWorkflowService from '@/services/api/NewsletterWorkflowService'

export default {

  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BForm,
    BButton,
    BFormGroup,
    BInputGroup,
    BFormTextarea,
    BCardText,
    BFormInput,
    BFormDatepicker,
    // validations
    ValidationProvider,
    ValidationObserver,
    Ripple,
    SelectSuggestion,
    NewsletterPopup
  },
  props: {
    id: {
      default: null,
      type: Number
    }
  },

  data: () => ({
    service: null,
    timelines: [],
    showNewsletterPopup: false,
    showCheckpointPopup: false,
    currentTimelineItem: null,
    checkpointDates: [],
    minDate: new Date().toISOString().split('T')[0],
    canSaveTimeline: false,
  }),
  computed: {
    validateForm() {
      return !this.isEmpty(this.model.name)
    }
  },
  beforeMount() {
    this.service = NewsletterWorkflowService.build(this.$vs)
  },
  mounted() {
    if (this.id) {
      this.loadData(this.id)
    }
  },

  methods: {
    requiredClass(whichData) {
      return !this.isEmpty(whichData) ? 'w-full' : 'w-full required'
    },
    loadData(id) {
      this.$vs.loading()

      this.service.getAllTimelines(id).then(
        response => {
          // Converte datas ISO para formato YYYY-MM-DD
          this.timelines = response.map(item => ({
            ...item,
            start_at: item.start_at ? this.convertIsoToDate(item.start_at) : '',
            end_at: item.end_at ? this.convertIsoToDate(item.end_at) : ''
          }))
          // Verifica se pode salvar após carregar os dados
          this.checkCanSave()
        },
        error => {

        }
      ).finally(
        () => {
          this.$vs.loading.close()
        }
      )
    },
    saveTimeline() {
      this.$vs.loading()

      const modelData = {
        workflow_id: this.id,
        timelines: this.timelines.map(item => ({
          id: item.id,
          newsletter_id: item.newsletter.id,
          start_at: item.start_at,
          end_at: item.end_at,
          notes: item.notes,
          checkpoints: item.checkpoints
        }))
      }

      this.service.saveTimeline(this.id, modelData).then(
        data => {
          this.notifySuccess(this.$vs, this.$t('messages.success'))
        },
        error => {
          this.showErrors(this.$validator, error)
        }
      ).finally(() => { this.$vs.loading.close() })
    },
    goBack() {
      this.$router.push({
        name: 'newsletter-workflow-index'
      })
    },
    isAdmin() {
      return isAdmin()
    },
    isEdit() {
      return !_.isNil(this.id)
    },
    selectedItems(items) {

      let newItems = []
      //Normalize the items
      items.forEach(item => {
        let newItem = {
          start_at: '',
          end_at: '',
          newsletter_id: item.id,
          notes: '',
          checkpoints: [],
          newsletter: {
          id: item.id,
          title: item.title,
          }
        }
        newItems.push(newItem)
      })

      // Usa $set para garantir reatividade
      const newTimelines = [...this.timelines, ...newItems]
      this.$set(this, 'timelines', newTimelines)

      // Verifica se pode salvar
      this.checkCanSave()
    },

    confirmRemoveItem(item) {
      // Monta o texto com período se as datas estiverem configuradas
      let periodText = ''
      if (item.start_at && item.end_at) {
        const startDate = this.formatDateDisplay(item.start_at)
        const endDate = this.formatDateDisplay(item.end_at)
        periodText = ` (Período: ${startDate} até ${endDate})`
      } else if (item.start_at) {
        const startDate = this.formatDateDisplay(item.start_at)
        periodText = ` (Início: ${startDate})`
      } else if (item.end_at) {
        const endDate = this.formatDateDisplay(item.end_at)
        periodText = ` (Fim: ${endDate})`
      }

      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Confirmar Exclusão',
        text: `Tem certeza que deseja remover "${item.newsletter.title}" do cronograma?${periodText}`,
        accept: () => this.removeItem(item),
        acceptText: 'Sim, remover',
        cancelText: 'Cancelar'
      })
    },

    removeItem(item) {
      console.log('=== removeItem DEBUG ===')
      console.log('item:', item)
      console.log('item.id:', item.id, 'type:', typeof item.id)
      console.log('this.id:', this.id, 'type:', typeof this.id)

      const index = this.timelines.findIndex(t => t.id === item.id)
      console.log('index:', index)

      if (index > -1) {
        // Se o item tem ID, chama o endpoint de exclusão
        if (item.id && this.id) {
          console.log('Chamando endpoint deleteTimeline com:', this.id, item.id)
          this.$vs.loading()
          this.service.deleteTimeline(this.id, item.id).then(
            () => {
              console.log('Endpoint deleteTimeline sucesso')
              // Remove da lista local após sucesso no servidor
              this.$delete(this.timelines, index)
              this.$vs.notify({
                color: 'success',
                title: 'Sucesso',
                text: 'Item removido do cronograma',
                iconPack: 'feather',
                icon: 'icon-check-circle',
              })
              // Verifica se pode salvar
              this.checkCanSave()
            },
            error => {
              console.log('Endpoint deleteTimeline erro:', error)
              this.$vs.notify({
                color: 'danger',
                title: 'Erro',
                text: 'Erro ao remover item do cronograma',
                iconPack: 'feather',
                icon: 'icon-alert-circle',
              })
            }
          ).finally(
            () => {
              this.$vs.loading.close()
            }
          )
        } else {
          console.log('Removendo apenas da lista local (sem ID ou sem this.id)')
          // Se não tem ID, remove apenas da lista local
          this.$delete(this.timelines, index)
          this.$vs.notify({
            color: 'success',
            title: 'Sucesso',
            text: 'Item removido do cronograma'
          })
          // Verifica se pode salvar
          this.checkCanSave()
        }
      }
      console.log('=== END removeItem DEBUG ===')
    },

    convertIsoToDate(isoString) {
      if (!isoString) return ''

      try {
        const date = new Date(isoString)
        if (isNaN(date.getTime())) return ''

        // Retorna no formato YYYY-MM-DD para o input date
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')

        return `${year}-${month}-${day}`
      } catch (error) {
        console.error('Erro ao converter data ISO:', error)
        return ''
      }
    },

    convertDateToIso(dateString) {
      if (!dateString) return null

      try {
        // Para datas no formato YYYY-MM-DD, cria a data localmente para evitar problemas de fuso horário
        if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = dateString.split('-').map(Number)
          const date = new Date(year, month - 1, day) // month é 0-indexed

          // Converte para ISO string, mas mantém a data local
          const isoString = date.toISOString()
          console.log(`convertDateToIso: ${dateString} → ${isoString}`)
          return isoString
        } else {
          // Para outros formatos, usa conversão normal
          const date = new Date(dateString)
          if (isNaN(date.getTime())) return null
          return date.toISOString()
        }
      } catch (error) {
        console.error('Erro ao converter data para ISO:', error)
        return null
      }
    },

    validateStartDate(item) {
      // Se a data de início for maior que a data de fim, ajusta a data de fim
      if (item.start_at && item.end_at && item.start_at > item.end_at) {
        item.end_at = item.start_at
      }
      // Verifica se pode salvar
      this.checkCanSave()
    },

    validateEndDate(item) {
      // Se a data de fim for menor que a data de início, ajusta para ser igual
      if (item.start_at && item.end_at && item.end_at < item.start_at) {
        item.end_at = item.start_at
      }
      // Verifica se pode salvar
      this.checkCanSave()
    },

    checkCanSave() {
      // Verifica se há itens na timeline
      if (this.timelines.length === 0) {
        this.canSaveTimeline = false
        return
      }

      // Verifica se todos os itens têm datas de início e fim preenchidas
      const allValid = this.timelines.every(item => {
        const hasStart = item.start_at && item.start_at.toString().trim() !== ''
        const hasEnd = item.end_at && item.end_at.toString().trim() !== ''
        return hasStart && hasEnd
      })

      this.canSaveTimeline = allValid
      console.log('checkCanSave:', allValid, 'timelines:', this.timelines.length)
    },

    // Métodos para gerenciar checkpoints
    openCheckpointPopup(item) {
      this.currentTimelineItem = item
      this.loadCheckpointDates(item)
      this.showCheckpointPopup = true
    },

    closeCheckpointPopup() {
      this.showCheckpointPopup = false
      this.currentTimelineItem = null
      this.checkpointDates = []
    },

    loadCheckpointDates(item) {
      // Carrega as datas de checkpoint do item atual
      if (item.checkpoints && Array.isArray(item.checkpoints)) {
        this.checkpointDates = item.checkpoints.map(checkpoint => ({
          date: checkpoint.checkpoint_at ? this.convertIsoToDate(checkpoint.checkpoint_at) : '',
          id: checkpoint.id || null
        }))
      } else {
        this.checkpointDates = []
      }
    },

    addNewCheckpoint() {
      this.checkpointDates.push({
        date: '',
        id: null
      })
    },

    removeCheckpoint(index) {
      this.checkpointDates.splice(index, 1)
    },

    validateCheckpointDate(checkpoint, index) {
      // Valida se a data não está vazia e está dentro do período da timeline
      if (checkpoint.date && this.currentTimelineItem) {
        const startDate = this.currentTimelineItem.start_at
        const endDate = this.currentTimelineItem.end_at

        // Compara as datas como strings no formato YYYY-MM-DD para evitar problemas de fuso horário
        if (startDate && checkpoint.date < startDate) {
          this.$vs.notify({
            color: 'warning',
            title: 'Aviso',
            text: 'A data de verificação não pode ser anterior ao início do cronograma!',
            iconPack: 'feather',
            icon: 'icon-alert-circle',
          })
          checkpoint.date = startDate
        }

        if (endDate && checkpoint.date > endDate) {
          this.$vs.notify({
            color: 'warning',
            title: 'Aviso',
            text: 'A data de verificação não pode ser posterior ao fim do cronograma!',
            iconPack: 'feather',
            icon: 'icon-alert-circle',
          })
          checkpoint.date = endDate
        }
      }
    },

     saveCheckpointDates() {
       if (!this.currentTimelineItem) return

       console.log('=== saveCheckpointDates DEBUG ===')
       console.log('checkpointDates:', this.checkpointDates)

       // Converte as datas para o formato esperado pelo backend
       const checkpointData = this.checkpointDates
         .filter(cp => cp.date && cp.date.trim() !== '')
         .map(cp => {
           const checkpoint = {
             checkpoint_at: cp.date ? this.convertDateToIso(cp.date) : null
           }

           // Se tem ID, inclui para atualização; se não tem, será criado novo
           if (cp.id) {
             checkpoint.id = cp.id
           }

           console.log('Processando checkpoint:', cp, '→', checkpoint)
           return checkpoint
         })

       console.log('checkpointData final:', checkpointData)

       // Atualiza o item na timeline
       const timelineIndex = this.timelines.findIndex(t => t.id === this.currentTimelineItem.id)
       if (timelineIndex > -1) {
         this.$set(this.timelines[timelineIndex], 'checkpoints', checkpointData)
         console.log('Timeline atualizada no índice:', timelineIndex)
       }

       // Verifica se pode salvar
       this.checkCanSave()

       this.$vs.notify({
         color: 'warning',
         title: 'Aviso',
         text: 'Datas dos pontos de verificação atualizadas. Favor, salvar as alterações.',
         iconPack: 'feather',
           icon: 'icon-alert-circle',
       })

       // Fecha o modal
       this.closeCheckpointPopup()
       console.log('=== END saveCheckpointDates DEBUG ===')
     },

    formatDateDisplay(value) {
      if (!value) return ''

      try {
        // Para datas no formato YYYY-MM-DD, cria a data localmente para evitar problemas de fuso horário
        let date
        if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
          // Formato YYYY-MM-DD - cria data local
          const [year, month, day] = value.split('-').map(Number)
          date = new Date(year, month - 1, day) // month é 0-indexed
        } else {
          // Outros formatos - usa new Date normal
          date = new Date(value)
        }

        if (isNaN(date.getTime())) return ''

        // Mapeamento de dias da semana abreviados
        const days = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb']
        const dayName = days[date.getDay()]
        const day = String(date.getDate()).padStart(2, '0')
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const year = date.getFullYear()

        return `${dayName}, ${day}/${month}/${year}`
      } catch (error) {
        console.error('Erro ao formatar data:', error)
        return ''
      }
    },

    canManageCheckpoints(item) {
      // Verifica se o item tem datas de início e fim preenchidas
      const hasStart = item.start_at && item.start_at.toString().trim() !== ''
      const hasEnd = item.end_at && item.end_at.toString().trim() !== ''
      return hasStart && hasEnd
    },
  }
}
</script>

<style scoped>
/* Lista Compacta - Estilo Planilha */
.compact-list {
  border: 1px solid #e9ecef;
  border-radius: 4px;
  overflow: visible;
}

.list-header {
  background-color: #f8f9fa;
  padding: 8px 12px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
}

.list-title {
  color: #333;
}

.list-counter {
  color: #666;
  font-weight: normal;
}

.list-items {
  overflow: visible;
}

.list-item {
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
  position: relative;
}

.list-item:hover {
  background-color: #f8f9fa;
}

.list-item:last-child {
  border-bottom: none;
}

.item-content {
  display: grid;
  grid-template-columns: 40px 1fr 300px 120px;
  align-items: center;
  padding: 12px;
  gap: 12px;
}

.item-counter {
  font-size: 12px;
  color: #666;
  text-align: center;
  font-weight: 500;
}

.item-title {
  font-size: 14px;
  color: #333;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-dates {
  display: flex;
  gap: 12px;
  position: relative;
  z-index: 10;
}

.date-field {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.date-label {
  font-size: 11px;
  color: #666;
  font-weight: 500;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.date-input {
  font-size: 12px;
  padding: 6px 8px;
  height: 32px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #fff;
}

.date-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  outline: none;
}

.item-actions {
  display: flex;
  gap: 4px;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: nowrap;
  min-width: 100px;
}

.item-actions .btn {
  padding: 4px 8px;
  font-size: 12px;
  min-width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dropdown-aligned {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  width: 28px;
}

.dropdown-icon {
  margin: 0 !important;
}

.dropdown-remove-item {
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.dropdown-remove-item:hover {
  background-color: #fef2f2;
}

.dropdown-remove-item .text-danger {
  color: #dc2626 !important;
}

/* Responsive para mobile - layout vertical */
@media (max-width: 768px) {
  .item-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px;
  }

  .item-counter {
    order: 1;
    text-align: left;
    font-size: 14px;
    font-weight: 600;
    color: #007bff;
  }

  .item-title {
    order: 2;
    font-size: 16px;
    font-weight: 600;
    color: #333;
    white-space: normal;
    overflow: visible;
    text-overflow: initial;
    word-wrap: break-word;
    hyphens: auto;
    -webkit-hyphens: auto;
    -ms-hyphens: auto;
  }

  .item-dates {
    order: 3;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .date-field {
    gap: 4px;
  }

  .date-label {
    font-size: 12px;
    font-weight: 500;
  }

  .date-input {
    font-size: 14px;
    padding: 8px 10px;
    height: 36px;
  }

  .item-actions {
    order: 4;
    justify-content: flex-start;
    min-width: auto;
    gap: 8px;
    margin-top: 8px;
  }

  .item-actions .btn {
    min-width: 32px;
    height: 32px;
    padding: 6px 8px;
    font-size: 12px;
  }

  .dropdown-aligned {
    height: 32px;
    width: 32px;
  }
}

/* Responsive para telas muito pequenas */
@media (max-width: 374px) {
  .item-content {
    padding: 8px;
    gap: 8px;
  }

  .item-title {
    font-size: 14px;
    line-height: 1.3;
  }

  .item-dates {
    gap: 8px;
  }

  .date-field {
    gap: 3px;
  }

  .date-label {
    font-size: 11px;
  }

  .date-input {
    font-size: 13px;
    padding: 6px 8px;
    height: 32px;
  }

  .item-actions {
    gap: 6px;
    margin-top: 6px;
  }

  .item-actions .btn {
    min-width: 28px;
    height: 28px;
    padding: 4px 6px;
    font-size: 11px;
  }

  .dropdown-aligned {
    height: 28px;
    width: 28px;
  }

  .dropdown-remove-item {
    padding: 6px 8px;
  }
}

/* Estilos para o popup de checkpoint */
.checkpoint-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 8px;
}

.checkpoint-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 8px;
}

.checkpoint-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.checkpoint-date {
  flex: 1;
}

.checkpoint-date .form-control {
  font-size: 14px;
  padding: 6px 8px;
  height: 36px;
}

.checkpoint-actions {
  display: flex;
  gap: 4px;
}

.checkpoint-actions .btn {
  padding: 4px 8px;
  font-size: 12px;
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-checkpoints {
  padding: 20px;
  text-align: center;
}

 .no-checkpoints p {
   margin: 0;
   font-style: italic;
 }

 /* Estilos para o período do cronograma */
 .timeline-period {
   background-color: #f8f9fa;
   border: 1px solid #e9ecef;
   border-radius: 6px;
   padding: 12px;
 }

 .period-info {
   display: flex;
   align-items: center;
   gap: 8px;
   margin-bottom: 8px;
 }

 .period-info i {
   font-size: 16px;
 }

 .period-label {
   font-weight: 500;
   color: #495057;
 }

 .period-dates {
   display: flex;
   align-items: center;
   gap: 8px;
   color: #007bff;
 }

 .period-separator {
   color: #6c757d;
   font-weight: normal;
 }

 .period-note {
   display: flex;
   align-items: center;
   gap: 6px;
 }

 .period-note i {
   font-size: 14px;
   color: #6c757d;
 }
</style>
